import { createSelector } from 'reselect';
import { has, values } from 'lodash';

import {
  GroupedBoundingPoly,
  buildTimelineCollection,
} from '../timeline-collection';
import { selectUdrAssetNotNil } from './udrs';
import { selectMediaDuration, selectFrameRate } from './view';
import { UDRsPolyAssetGroup } from '@common/state/modules/mediaDetails/models';
import {
  isTextOnlyOverlayGroup,
  mapUdrGroupToGroupedBoundingPolys,
} from '@helpers/textOverlay';

function buildCollectionFromGroups(
  groups: ReadonlyArray<UDRsPolyAssetGroup | null>,
  mediaDuration: number,
  fps: number | undefined
) {
  const polys: GroupedBoundingPoly[] = [];

  for (const group of groups) {
    if (!group) {
      continue;
    }
    polys.push(...mapUdrGroupToGroupedBoundingPolys(group));
  }

  return buildTimelineCollection(polys, 0, mediaDuration, 1, Date.now(), fps);
}

const selectUdrAssetGroups = createSelector(selectUdrAssetNotNil, (udrAsset) =>
  has(udrAsset, 'boundingPolys') ? values(udrAsset.boundingPolys) : []
);

export const selectUDRCollection = createSelector(
  selectMediaDuration,
  selectUdrAssetGroups,
  selectFrameRate,
  (mediaDuration, groups, fps) =>
    buildCollectionFromGroups(
      groups.filter(
        (group): group is UDRsPolyAssetGroup =>
          !!group && !isTextOnlyOverlayGroup(group)
      ),
      mediaDuration,
      fps
    )
);

export const selectTextOverlayCollection = createSelector(
  selectMediaDuration,
  selectUdrAssetGroups,
  selectFrameRate,
  (mediaDuration, groups, fps) =>
    buildCollectionFromGroups(
      groups.filter(
        (group): group is UDRsPolyAssetGroup =>
          !!group && isTextOnlyOverlayGroup(group)
      ),
      mediaDuration,
      fps
    )
);
